import type { AudioEngine, SoundKey } from "../audio/types";
import type { Song } from "./types";

/**
 * Song player with two modes:
 *
 * - "listen": auto-play with a scheduler over an injectable clock; it emits
 *   `onNoteOn(key)` for the visual guide (lit key).
 * - "play": the child plays — `expectedKey()` exposes the note playing now,
 *   `handleInput(key)` advances on a hit. No failure state: at ages 2-6
 *   the error is ignored, not punished (agreed didactics).
 *
 * The clock is injected (`now`/`setTimeout`) so that the bun tests use a
 * fake clock without depending on real timers.
 */

export type SongPlayerMode = "listen" | "play";

export interface SongPlayerCallbacks {
  /** Note that starts sounding / must light up (null in rests). */
  onNoteOn?: (key: SoundKey | null, index: number) => void;
  /** The song ended (auto-play) or the child played the last note. */
  onFinished?: () => void;
}

export interface SongPlayerClock {
  now: () => number;
  /** Schedules `fn` in `ms` and returns its canceller (no handle types). */
  schedule: (fn: () => void, ms: number) => () => void;
}

const realClock: SongPlayerClock = {
  now: () => performance.now(),
  schedule: (fn, ms) => {
    const id = setTimeout(fn, ms);
    return () => {
      clearTimeout(id);
    };
  },
};

const MS_PER_MINUTE = 60_000;

export interface SongPlayer {
  readonly mode: SongPlayerMode;
  /** Index of the current note (guide and progress). */
  position: () => number;
  /** "play" mode: expected note now; null if finished or in listen mode. */
  expectedKey: () => SoundKey | null;
  /** "listen" mode: starts auto-play. */
  start: () => void;
  /** "play" mode: child input. Returns true if it was the expected note. */
  handleInput: (key: SoundKey) => boolean;
  /** Stops timers and cuts voices. */
  stop: () => void;
}

export function createSongPlayer(
  engine: AudioEngine,
  song: Song,
  mode: SongPlayerMode,
  callbacks: SongPlayerCallbacks = {},
  clock: SongPlayerClock = realClock,
): SongPlayer {
  const beatMs = MS_PER_MINUTE / song.bpm;
  let index = 0;
  let cancelPending: (() => void) | null = null;
  let stopped = false;

  /** In "play" mode rests are not "played": it skips to the next sound. */
  const skipRests = () => {
    while (index < song.notes.length && song.notes[index]?.key === null) {
      index += 1;
    }
  };

  const scheduleNext = () => {
    if (stopped || index >= song.notes.length) {
      callbacks.onFinished?.();
      return;
    }
    const note = song.notes[index];
    if (!note) {
      return;
    }
    if (note.key !== null) {
      engine.playNote(song.instrument, note.key);
    }
    callbacks.onNoteOn?.(note.key, index);
    index += 1;
    cancelPending = clock.schedule(scheduleNext, note.beats * beatMs);
  };

  if (mode === "play") {
    skipRests();
  }

  return {
    mode,
    position: () => index,
    expectedKey: () => {
      if (mode !== "play" || index >= song.notes.length) {
        return null;
      }
      return song.notes[index]?.key ?? null;
    },
    start: () => {
      if (mode !== "listen" || stopped) {
        return;
      }
      scheduleNext();
    },
    handleInput: (key: SoundKey) => {
      if (mode !== "play" || index >= song.notes.length) {
        return false;
      }
      const expected = song.notes[index]?.key;
      if (key !== expected) {
        return false; // forgiving: note plays (triggered by keyboard), but does not advance
      }
      index += 1;
      skipRests();
      callbacks.onNoteOn?.(key, index - 1);
      if (index >= song.notes.length) {
        callbacks.onFinished?.();
      }
      return true;
    },
    stop: () => {
      stopped = true;
      cancelPending?.();
      cancelPending = null;
      engine.allNotesOff();
    },
  };
}
